import { hasRole } from "@data/utility/ability"
import LiveEventBuffer from "@data/services/LiveEventBuffer"
import { RoomEvent, Track } from "livekit-client"
import { CONNECT_OPTIONS } from "../../../engine/live/room/roomOptions"
import { useCallback, useEffect, useRef } from "react"
import { AppState } from "react-native"

// Tunables
const MAX_RECOVERIES_PER_SESSION = 2 // anti-loop: a rejoin that lands back in a ghost state must not storm
const RECOVERY_COOLDOWN_MS = 30 * 1000
const FOREGROUND_SETTLE_MS = 1500 // let the frozen WebRTC stack thaw before judging
const STATS_SAMPLE_GAP_MS = 2500

/**
 * Ghost-participant auto-recovery (web only — wired from the web LKEvents).
 *
 * An RTCPeerConnection can die silently while backgrounded: the SFU times the
 * participant out server-side, but the client still believes it is connected
 * (`room.state === "connected"`), so no audio flows and LiveKit's own reconnect never
 * fires. We therefore cannot trust `room.state` here — we look at the *real media flow*.
 *
 * Triggers:
 *   1. Return to foreground: if every subscribed remote mic track stops receiving
 *      packets (getRTCStatsReport), the media path is dead → recover. Applies to a
 *      student in dialogue mode AND to a tutor (a tutor is always meant to be hearing
 *      the room — confirmed live case 03/06/2026 occ. 143430, tutor on web Chrome with
 *      remote video arriving at ~1 Mbps and 0% packet loss but no audible mic).
 *   2. A server `force_reconnect` DataChannel signal (tutor toggled a ghost student) —
 *      student-only, the backend only pushes this against ghost students.
 * There is deliberately no "verify the audio came back after a reconnection" trigger:
 * a session reconnected by the SDK but silent has never been observed, and the one
 * report that looked like it was fully explained by the room being dead. A speculative
 * net is not worth its false positives — if the case exists, the traces will show it.
 *
 * Recovery = a clean `room.disconnect(true)` **followed by our own `room.connect()`**.
 * The rejoin evicts the stale SFU ghost. The explicit reconnect is not optional, and it is
 * the ONLY thing that brings the participant back: LiveKit never reconnects after a
 * disconnect the client asked for (`reconnectPolicy` only covers connections it lost by
 * itself), and no screen schedules one anymore. The 60s session poll is not a fallback
 * either — `LiveMainLayout.successCallback` only rebuilds on a genuine room *switch*, and
 * `room.roomInfo` survives `disconnect()` (the SDK assigns it in `applyJoinResponse` and
 * never clears it), so its three guards all stay false within the same session. If this
 * connect doesn't happen, nothing else runs.
 *
 * A concentré student receives no tutor audio by design, so detection is gated to dialogue
 * mode for students to avoid a permanent false positive; a tutor has no such gate.
 */
export function useGhostParticipantRecovery({
    room,
    user,
    isSoundMode,
    isConnected,
    roomData,
}) {
    const isStudent = hasRole(user, "student")
    const isTutor = hasRole(user, "tutor")
    // A student must be in dialogue (otherwise no remote audio is expected by design);
    // a tutor is always meant to hear the room, so no soundMode gate applies.
    const isMonitored = (isStudent && isSoundMode) || isTutor
    const recoveryCountRef = useRef(0)
    const lastRecoveryAtRef = useRef(0)
    const recoveringRef = useRef(false)
    const prevAppStateRef = useRef(AppState.currentState)

    const recover = useCallback(
        async (reason) => {
            if (!room || recoveringRef.current) return
            if (recoveryCountRef.current >= MAX_RECOVERIES_PER_SESSION) return
            if (Date.now() - lastRecoveryAtRef.current < RECOVERY_COOLDOWN_MS) return

            recoveringRef.current = true
            recoveryCountRef.current += 1
            lastRecoveryAtRef.current = Date.now()

            // Rare, observable event for QA + the success metric.
            LiveEventBuffer.getInstance().record("auto_reconnect", { reason })
            LiveEventBuffer.getInstance().flush()

            try {
                // Clean teardown → LiveKit evicts the stale ghost participant.
                await room.disconnect(true)
                // …and we rejoin ourselves. Nobody else will: the SDK does not
                // reconnect a disconnect we asked for, and the screens no longer
                // schedule one.
                if (roomData?.serverUrl && roomData?.token) {
                    // CONNECT_OPTIONS, pas `{}` : `{}` retombe sur le défaut du
                    // SDK (`maxRetries: 1`). C'est le connect le plus exposé —
                    // on vient de déconnecter l'utilisateur et rien d'autre ne
                    // le ramènera, donc un échec transitoire le laisse dehors.
                    await room.connect(
                        roomData.serverUrl,
                        roomData.token,
                        CONNECT_OPTIONS
                    )
                } else {
                    // On a déjà brûlé une tentative et déconnecté l'utilisateur :
                    // sortir en silence ici, c'est le laisser dehors sans trace.
                    console.log(
                        "Ghost recovery: pas de roomData, rejoin impossible",
                        { reason }
                    )
                }
            } catch (e) {
                console.log("Ghost recovery: rejoin failed", e)
            } finally {
                // Release the in-flight guard after a cooldown so a still-ghost rejoin
                // can be retried (bounded by MAX_RECOVERIES_PER_SESSION).
                setTimeout(() => {
                    recoveringRef.current = false
                }, RECOVERY_COOLDOWN_MS)
            }
        },
        [room, roomData?.serverUrl, roomData?.token]
    )

    // Is the student's incoming audio actually flowing? We look at *all* subscribed,
    // unmuted remote mic tracks and judge the path dead only if NONE received any packet
    // over the window. A live transport keeps ticking even during silence (Opus DTX
    // comfort-noise packets), whereas a ghosted subscriber goes flat zero — so this
    // doesn't mistake a quiet tutor (or a single silent peer in a group) for a ghost.
    const isRemoteAudioDead = useCallback(async () => {
        if (!room?.remoteParticipants) return false

        const tracks = []
        for (const [, participant] of room.remoteParticipants) {
            for (const [, publication] of participant.trackPublications) {
                if (publication.source !== Track.Source.Microphone) continue
                if (publication.isMuted) continue
                if (!publication.isSubscribed || !publication.track) continue
                if (typeof publication.track.getRTCStatsReport === "function") {
                    tracks.push(publication.track)
                }
            }
        }
        // No subscribed remote audio to judge → don't act.
        if (tracks.length === 0) return false

        try {
            const before = await Promise.all(tracks.map(readInboundAudioPackets))
            await wait(STATS_SAMPLE_GAP_MS)
            const after = await Promise.all(tracks.map(readInboundAudioPackets))
            return tracks.every((_, i) => {
                const b = before[i]
                const a = after[i]
                return b != null && a != null && a - b <= 0
            })
        } catch (e) {
            return false
        }
    }, [room])

    const maybeRecoverOnForeground = useCallback(async () => {
        // Only a participant who *should* be hearing the room — student in dialogue
        // mode, or any tutor (always expected to hear).
        if (!isMonitored || !isConnected || !room) return
        await wait(FOREGROUND_SETTLE_MS)
        if (await isRemoteAudioDead()) recover("foreground_media_stall")
    }, [isMonitored, isConnected, room, isRemoteAudioDead, recover])

    // Trigger 1 — return to foreground (web: AppState "active" maps to visibilitychange).
    useEffect(() => {
        if (!isMonitored) return
        const subscription = AppState.addEventListener("change", (nextAppState) => {
            const wasBackgrounded = /inactive|background/.test(
                prevAppStateRef.current
            )
            prevAppStateRef.current = nextAppState
            if (wasBackgrounded && nextAppState === "active") {
                maybeRecoverOnForeground()
            }
        })
        return () => subscription?.remove()
    }, [isMonitored, maybeRecoverOnForeground])

    // Trigger 2 — server-signaled force_reconnect (tutor toggled a ghost student).
    useEffect(() => {
        if (!isStudent || !room) return
        const handleData = (payload) => {
            try {
                const message = JSON.parse(new TextDecoder().decode(payload))
                if (message?.type !== "force_reconnect") return
                const targetIds = message?.data?.user_ids ?? []
                if (targetIds.includes(user?.id)) recover("server_signal")
            } catch (e) {
                // Unrelated / non-JSON data packet — ignore.
            }
        }
        room.on(RoomEvent.DataReceived, handleData)
        return () => room.off(RoomEvent.DataReceived, handleData)
    }, [isStudent, room, user?.id, recover])

}

async function readInboundAudioPackets(track) {
    try {
        const report = await track.getRTCStatsReport()
        if (!report) return null
        for (const [, stat] of report) {
            if (
                stat.type === "inbound-rtp" &&
                (stat.kind === "audio" || stat.mediaType === "audio")
            ) {
                return stat.packetsReceived ?? 0
            }
        }
    } catch (e) {
        return null
    }
    return null
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))
